// src/cockpit4/components/WorkflowEfficiencyAnalyzer.tsx
// ============================================
// 流程效率分析 - 工作流中心 P1-功能5
//
// 功能（改进计划文档 9.2 功能5）：
//   1. 流程耗时分析（每个节点 / 每个步骤的 avg / max / min / p95）
//   2. 瓶颈节点识别（含瓶颈分数 0-100，红色标记）
//   3. 成功率和失败率分析（节点级 + 流程级）
//   4. 优化建议和效果预测（基于瓶颈 / 高失败率 / 慢执行自动生成）
//   5. 流程 benchmark 对比（行业平均 / 标杆水平）
//   6. 效率趋势分析（recharts LineChart，按日展示耗时与成功率变化）
//
// 数据来源（白皮书铁律一：禁止假数据）：
//   - 执行记录：localStorage(key: xeaos_workflow_executions) 真实执行记录聚合
//   - 类型复用自 WorkflowMonitor（WorkflowExecution / NodeExecution / ExecutionStatus）
//   无数据时显示"暂无执行数据 · 请先运行工作流"，绝不展示假数据
//
// 瓶颈识别算法：
//   瓶颈分数 = (平均耗时/总耗时) * 0.4 + 失败率 * 0.4 + (执行次数占比) * 0.2
//   - durationRatio = node.avgDuration / workflow.avgTotalDuration（节点耗时占流程总耗时比例）
//   - failureRate   = 1 - node.successRate（节点失败率）
//   - executionRatio = node.totalExecutions > 0 ? 1 : 0（节点是否参与执行）
//   分数 ≥ 50 判定为瓶颈节点，结果 clamp 到 0-100
// ============================================

import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Activity,
  AlertTriangle,
  Award,
  BarChart3,
  CheckCircle2,
  ChevronDown,
  Clock,
  Cpu,
  Filter,
  Layers,
  Lightbulb,
  Loader2,
  Snail,
  Target,
  TrendingUp,
  Zap,
} from "lucide-react";
import type {
  ExecutionStatus,
  WorkflowExecution,
} from "./WorkflowMonitor";

// ============================================
// 类型定义（与改进计划文档数据模型对齐）
// ============================================

/** 节点效率 */
export interface NodeEfficiency {
  nodeId: string;
  nodeName: string;
  nodeType: string;
  totalExecutions: number;
  successCount: number;
  failedCount: number;
  successRate: number;
  avgDuration: number;
  maxDuration: number;
  minDuration: number;
  p95Duration: number; // 95 分位耗时
  isBottleneck: boolean; // 是否瓶颈
  bottleneckScore: number; // 瓶颈分数（0-100）
}

/** 工作流效率 */
export interface WorkflowEfficiency {
  workflowId: string;
  workflowName: string;
  totalExecutions: number;
  successRate: number;
  avgTotalDuration: number;
  nodeEfficiencies: NodeEfficiency[];
  bottleneckNodes: NodeEfficiency[]; // 瓶颈节点列表
  efficiencyTrend: { date: string; duration: number; successRate: number }[];
}

/** 优化建议 */
export interface OptimizationSuggestion {
  nodeId: string;
  nodeName: string;
  type: "high_failure" | "slow_execution" | "bottleneck" | "low_success";
  title: string;
  detail: string;
  predictedImprovement: string; // 预期改善
  priority: "high" | "medium" | "low";
}

/** 行业 benchmark */
export interface Benchmark {
  metric: string;
  current: number;
  industryAvg: number;
  bestInClass: number;
  status: "above" | "average" | "below";
}

// ============================================
// 常量
// ============================================

const STORAGE_KEY = "xeaos_workflow_executions";

/** 瓶颈判定阈值（分数 ≥ 此值视为瓶颈） */
const BOTTLENECK_THRESHOLD = 50;

/** 内置行业 benchmark（current 由真实数据填充） */
const BENCHMARKS: Benchmark[] = [
  { metric: "平均流程耗时", current: 0, industryAvg: 300, bestInClass: 180, status: "average" },
  { metric: "流程成功率", current: 0, industryAvg: 0.92, bestInClass: 0.98, status: "average" },
  { metric: "瓶颈节点占比", current: 0, industryAvg: 0.15, bestInClass: 0.05, status: "average" },
  { metric: "自动化率", current: 0, industryAvg: 0.7, bestInClass: 0.9, status: "average" },
];

/** 已完成状态集合（参与成功率 / 耗时统计） */
const COMPLETED_STATUS: ExecutionStatus[] = [
  "success",
  "failed",
  "timeout",
  "cancelled",
];

/** 失败状态集合 */
const FAILED_STATUS: ExecutionStatus[] = ["failed", "timeout"];

// ============================================
// 工具函数
// ============================================

/** 毫秒转可读时长 */
function formatDuration(ms?: number): string {
  if (ms === undefined || ms === null || isNaN(ms)) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60000);
  const s = Math.round((ms % 60000) / 1000);
  return `${m}m${s}s`;
}

/** 读取 localStorage 执行记录 */
function loadExecutions(): WorkflowExecution[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as WorkflowExecution[]) : [];
  } catch {
    return [];
  }
}

/**
 * 瓶颈分数计算
 * 瓶颈分数 = (平均耗时/总耗时) * 0.4 + 失败率 * 0.4 + (执行次数占比) * 0.2
 * 结果 clamp 到 0-100
 */
function calculateBottleneckScore(
  node: NodeEfficiency,
  totalDuration: number
): number {
  const durationRatio = totalDuration > 0 ? node.avgDuration / totalDuration : 0;
  const failureRate = 1 - node.successRate;
  const executionRatio = node.totalExecutions > 0 ? 1 : 0;
  const raw =
    (durationRatio * 0.4 + failureRate * 0.4 + executionRatio * 0.2) * 100;
  return Math.round(Math.min(100, Math.max(0, raw)));
}

/** 计算分位数（线性插值法） */
function percentile(values: number[], p: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  if (sorted.length === 1) return sorted[0];
  const rank = (p / 100) * (sorted.length - 1);
  const lo = Math.floor(rank);
  const hi = Math.ceil(rank);
  if (lo === hi) return sorted[lo];
  const frac = rank - lo;
  return sorted[lo] * (1 - frac) + sorted[hi] * frac;
}

/** ISO 日期字符串转 MM/DD 标签 */
function formatDateLabel(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr.slice(5);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  } catch {
    return dateStr.slice(5);
  }
}

// ============================================
// 分析函数
// ============================================

/** 构建效率趋势数据（最近 14 天，按日聚合耗时与成功率） */
function buildEfficiencyTrend(
  executions: WorkflowExecution[]
): { date: string; duration: number; successRate: number }[] {
  const days = 14;
  const result: { date: string; duration: number; successRate: number }[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().slice(0, 10);
    const dayExecs = executions.filter(
      (e) => (e.startTime || "").slice(0, 10) === dateStr
    );
    const completed = dayExecs.filter((e) =>
      COMPLETED_STATUS.includes(e.status)
    );
    const successCount = dayExecs.filter((e) => e.status === "success").length;
    const durations = completed
      .map((e) => e.duration)
      .filter((dd): dd is number => typeof dd === "number" && dd > 0);
    const avgDur =
      durations.length > 0
        ? durations.reduce((s, dd) => s + dd, 0) / durations.length
        : 0;
    const sr =
      completed.length > 0 ? (successCount / completed.length) * 100 : 0;
    result.push({
      date: dateStr,
      duration: Math.round(avgDur),
      successRate: Math.round(sr * 10) / 10,
    });
  }
  return result;
}

/**
 * 聚合分析工作流效率
 * 按 nodeId 聚合执行记录，计算节点级效率指标与瓶颈分数
 */
function analyzeEfficiency(
  executions: WorkflowExecution[],
  workflowId: string,
  workflowName: string
): WorkflowEfficiency {
  // 流程级统计
  const completed = executions.filter((e) =>
    COMPLETED_STATUS.includes(e.status)
  );
  const successCount = executions.filter((e) => e.status === "success").length;
  const successRate =
    completed.length > 0 ? successCount / completed.length : 0;

  const durations = completed
    .map((e) => e.duration)
    .filter((d): d is number => typeof d === "number" && d > 0);
  const avgTotalDuration =
    durations.length > 0
      ? durations.reduce((s, d) => s + d, 0) / durations.length
      : 0;

  // 节点级聚合
  const nodeMap = new Map<
    string,
    {
      nodeId: string;
      nodeName: string;
      nodeType: string;
      durations: number[];
      successCount: number;
      failedCount: number;
      totalExecutions: number;
    }
  >();

  for (const exec of executions) {
    for (const node of exec.nodeExecutions) {
      // 跳过未完成节点（running / queued 不参与统计）
      if (
        node.status === "running" ||
        node.status === "queued" ||
        !COMPLETED_STATUS.includes(node.status)
      ) {
        continue;
      }
      const key = node.nodeId;
      const isSuccess = node.status === "success";
      const isFailed = FAILED_STATUS.includes(node.status);
      const hasDur =
        typeof node.duration === "number" && node.duration > 0;
      const existing = nodeMap.get(key);
      if (existing) {
        existing.totalExecutions += 1;
        if (isSuccess) existing.successCount += 1;
        if (isFailed) existing.failedCount += 1;
        if (hasDur) existing.durations.push(node.duration as number);
      } else {
        nodeMap.set(key, {
          nodeId: node.nodeId,
          nodeName: node.nodeName,
          nodeType: node.nodeType,
          durations: hasDur ? [node.duration as number] : [],
          successCount: isSuccess ? 1 : 0,
          failedCount: isFailed ? 1 : 0,
          totalExecutions: 1,
        });
      }
    }
  }

  // 计算每个节点的效率指标
  const nodeEfficiencies: NodeEfficiency[] = Array.from(nodeMap.values()).map(
    (n) => {
      const nodeSuccessRate =
        n.totalExecutions > 0 ? n.successCount / n.totalExecutions : 0;
      const avgDuration =
        n.durations.length > 0
          ? n.durations.reduce((s, d) => s + d, 0) / n.durations.length
          : 0;
      const maxDuration =
        n.durations.length > 0 ? Math.max(...n.durations) : 0;
      const minDuration =
        n.durations.length > 0 ? Math.min(...n.durations) : 0;
      const p95Duration = percentile(n.durations, 95);
      const node: NodeEfficiency = {
        nodeId: n.nodeId,
        nodeName: n.nodeName,
        nodeType: n.nodeType,
        totalExecutions: n.totalExecutions,
        successCount: n.successCount,
        failedCount: n.failedCount,
        successRate: nodeSuccessRate,
        avgDuration,
        maxDuration,
        minDuration,
        p95Duration,
        isBottleneck: false,
        bottleneckScore: 0,
      };
      node.bottleneckScore = calculateBottleneckScore(node, avgTotalDuration);
      node.isBottleneck = node.bottleneckScore >= BOTTLENECK_THRESHOLD;
      return node;
    }
  );

  // 瓶颈节点列表（按分数倒序）
  const bottleneckNodes = nodeEfficiencies
    .filter((n) => n.isBottleneck)
    .sort((a, b) => b.bottleneckScore - a.bottleneckScore);

  // 效率趋势
  const efficiencyTrend = buildEfficiencyTrend(executions);

  return {
    workflowId,
    workflowName,
    totalExecutions: executions.length,
    successRate,
    avgTotalDuration,
    nodeEfficiencies,
    bottleneckNodes,
    efficiencyTrend,
  };
}

/**
 * 基于效率分析生成优化建议
 * 规则：瓶颈节点 / 低成功率 / 高失败率 / 慢执行
 */
function generateSuggestions(
  efficiency: WorkflowEfficiency
): OptimizationSuggestion[] {
  const suggestions: OptimizationSuggestion[] = [];

  // 节点平均耗时基线（用于判定慢执行）
  const avgNodeDuration =
    efficiency.nodeEfficiencies.length > 0
      ? efficiency.nodeEfficiencies.reduce((s, n) => s + n.avgDuration, 0) /
        efficiency.nodeEfficiencies.length
      : 0;

  for (const node of efficiency.nodeEfficiencies) {
    const failureRate = 1 - node.successRate;

    // 瓶颈节点
    if (node.isBottleneck) {
      suggestions.push({
        nodeId: node.nodeId,
        nodeName: node.nodeName,
        type: "bottleneck",
        title: `瓶颈节点：${node.nodeName}`,
        detail: `瓶颈分数 ${node.bottleneckScore}/100，平均耗时 ${formatDuration(
          node.avgDuration
        )}，失败率 ${(failureRate * 100).toFixed(1)}%。建议拆分任务、增加并发或引入缓存。`,
        predictedImprovement: "预计可降低流程耗时 15-25%",
        priority: "high",
      });
    }

    // 低成功率（成功率 < 50%）
    if (node.successRate < 0.5 && node.totalExecutions >= 2) {
      suggestions.push({
        nodeId: node.nodeId,
        nodeName: node.nodeName,
        type: "low_success",
        title: `低成功率节点：${node.nodeName}`,
        detail: `成功率仅 ${(node.successRate * 100).toFixed(1)}%，共执行 ${node.totalExecutions} 次，失败 ${node.failedCount} 次。建议检查输入参数与外部依赖稳定性。`,
        predictedImprovement: "预计可提升节点成功率 20-40%",
        priority: "high",
      });
    } else if (node.successRate < 0.8 && node.totalExecutions >= 2) {
      // 高失败率（成功率 50%-80%）
      suggestions.push({
        nodeId: node.nodeId,
        nodeName: node.nodeName,
        type: "high_failure",
        title: `高失败率节点：${node.nodeName}`,
        detail: `失败率 ${(failureRate * 100).toFixed(1)}%，建议增加错误处理、退避重试与降级策略。`,
        predictedImprovement: "预计可提升成功率 10-20%",
        priority: "medium",
      });
    }

    // 慢执行（平均耗时 > 节点均值 2 倍且超过 1 秒）
    if (
      avgNodeDuration > 0 &&
      node.avgDuration > avgNodeDuration * 2 &&
      node.avgDuration > 1000
    ) {
      suggestions.push({
        nodeId: node.nodeId,
        nodeName: node.nodeName,
        type: "slow_execution",
        title: `慢执行节点：${node.nodeName}`,
        detail: `平均耗时 ${formatDuration(node.avgDuration)}，是节点均值的 ${(
          node.avgDuration / avgNodeDuration
        ).toFixed(1)} 倍，P95 达 ${formatDuration(node.p95Duration)}。建议优化执行逻辑或预计算结果。`,
        predictedImprovement: "预计可缩短节点耗时 30-50%",
        priority: "medium",
      });
    }
  }

  // 按优先级排序
  const priorityOrder: Record<OptimizationSuggestion["priority"], number> = {
    high: 0,
    medium: 1,
    low: 2,
  };
  suggestions.sort(
    (a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]
  );
  return suggestions.slice(0, 10);
}

/**
 * 计算行业 benchmark 对比
 * current 由真实数据填充，status 按指标方向（越高越好 / 越低越好）判定
 */
function computeBenchmarks(
  efficiency: WorkflowEfficiency,
  executions: WorkflowExecution[]
): Benchmark[] {
  // 平均流程耗时（秒）
  const avgDurationSec = efficiency.avgTotalDuration / 1000;
  // 流程成功率（0-1）
  const successRate = efficiency.successRate;
  // 瓶颈节点占比（0-1）
  const bottleneckRatio =
    efficiency.nodeEfficiencies.length > 0
      ? efficiency.bottleneckNodes.length /
        efficiency.nodeEfficiencies.length
      : 0;
  // 自动化率：非手动触发（schedule / event）占比
  const automatedCount = executions.filter(
    (e) => e.triggeredBy === "schedule" || e.triggeredBy === "event"
  ).length;
  const automationRate =
    executions.length > 0 ? automatedCount / executions.length : 0;

  return BENCHMARKS.map((b) => {
    let current = 0;
    let higherIsBetter = true;
    switch (b.metric) {
      case "平均流程耗时":
        current = avgDurationSec;
        higherIsBetter = false;
        break;
      case "流程成功率":
        current = successRate;
        higherIsBetter = true;
        break;
      case "瓶颈节点占比":
        current = bottleneckRatio;
        higherIsBetter = false;
        break;
      case "自动化率":
        current = automationRate;
        higherIsBetter = true;
        break;
      default:
        current = 0;
    }

    let status: Benchmark["status"];
    if (higherIsBetter) {
      if (current >= b.industryAvg) status = "above";
      else if (current >= b.industryAvg * 0.85) status = "average";
      else status = "below";
    } else {
      if (current <= b.industryAvg) status = "above";
      else if (current <= b.industryAvg * 1.15) status = "average";
      else status = "below";
    }

    return { ...b, current, status };
  });
}

// ============================================
// 子组件：KPI 卡片
// ============================================
interface MiniKpiProps {
  label: string;
  value: string | number;
  icon: typeof Activity;
  color: string;
  delay?: number;
}

function MiniKpi({ label, value, icon: Icon, color, delay = 0 }: MiniKpiProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay, ease: "easeOut" }}
      className="relative rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-md p-3 overflow-hidden"
    >
      {/* 顶部光带 */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{
          background: `linear-gradient(90deg, transparent, ${color}80, transparent)`,
        }}
      />
      {/* 角落光晕 */}
      <div
        className="absolute -top-8 -right-8 w-16 h-16 rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, ${color}20 0%, transparent 70%)`,
          filter: "blur(16px)",
        }}
      />
      <div className="flex items-center gap-2 mb-1.5 relative">
        <div
          className="w-6 h-6 rounded-md flex items-center justify-center"
          style={{ background: `${color}15`, border: `1px solid ${color}30` }}
        >
          <Icon size={12} style={{ color }} />
        </div>
        <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
          {label}
        </span>
      </div>
      <div className="text-[20px] font-mono font-bold text-white tracking-tight leading-none relative">
        {value}
      </div>
    </motion.div>
  );
}

// ============================================
// 子组件：空状态
// ============================================
function EmptyState({ text, sub }: { text: string; sub?: string }) {
  return (
    <div className="py-10 text-center">
      <div className="text-[12px] font-mono text-gray-600 italic">{text}</div>
      {sub && (
        <div className="text-[10px] font-mono text-gray-700 mt-1.5">{sub}</div>
      )}
    </div>
  );
}

// ============================================
// 主组件
// ============================================
export default function WorkflowEfficiencyAnalyzer() {
  // ===== 状态 =====
  const [executions, setExecutions] = useState<WorkflowExecution[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<string>("all");
  const [loading, setLoading] = useState<boolean>(true);

  // ===== 初始化加载（localStorage） =====
  useEffect(() => {
    setExecutions(loadExecutions());
    setLoading(false);
  }, []);

  // ===== 工作流列表（用于选择器） =====
  const workflowOptions = useMemo(() => {
    const map = new Map<string, number>();
    for (const exec of executions) {
      const name = exec.workflowName || "未命名工作流";
      map.set(name, (map.get(name) ?? 0) + 1);
    }
    return Array.from(map.entries()).map(([name, count]) => ({
      name,
      count,
    }));
  }, [executions]);

  // ===== 当前选中工作流的执行记录 =====
  const filteredExecutions = useMemo(() => {
    if (selectedWorkflow === "all") return executions;
    return executions.filter((e) => e.workflowName === selectedWorkflow);
  }, [executions, selectedWorkflow]);

  // ===== 效率分析（核心聚合） =====
  const efficiency = useMemo<WorkflowEfficiency>(() => {
    const wfId = selectedWorkflow;
    const wfName =
      selectedWorkflow === "all"
        ? "全部工作流"
        : selectedWorkflow || "未命名工作流";
    return analyzeEfficiency(filteredExecutions, wfId, wfName);
  }, [filteredExecutions, selectedWorkflow]);

  // ===== 优化建议 =====
  const suggestions = useMemo(
    () => generateSuggestions(efficiency),
    [efficiency]
  );

  // ===== 行业 benchmark 对比 =====
  const benchmarks = useMemo(
    () => computeBenchmarks(efficiency, filteredExecutions),
    [efficiency, filteredExecutions]
  );

  // ===== 趋势图数据（转秒便于阅读） =====
  const trendChartData = useMemo(
    () =>
      efficiency.efficiencyTrend.map((t) => ({
        ...t,
        label: formatDateLabel(t.date),
        durationSec: Math.round(t.duration / 100) / 10,
      })),
    [efficiency.efficiencyTrend]
  );

  // ===== 顶部 KPI =====
  const kpis = useMemo(() => {
    const automatedCount = filteredExecutions.filter(
      (e) => e.triggeredBy === "schedule" || e.triggeredBy === "event"
    ).length;
    const automationRate =
      filteredExecutions.length > 0
        ? (automatedCount / filteredExecutions.length) * 100
        : 0;
    return {
      avgDuration: efficiency.avgTotalDuration,
      successRate: efficiency.successRate * 100,
      bottleneckCount: efficiency.bottleneckNodes.length,
      automationRate,
    };
  }, [efficiency, filteredExecutions]);

  // ===== 节点效率排行（按平均耗时倒序） =====
  const rankedNodes = useMemo(
    () =>
      [...efficiency.nodeEfficiencies].sort(
        (a, b) => b.avgDuration - a.avgDuration
      ),
    [efficiency.nodeEfficiencies]
  );

  const hasData = executions.length > 0;
  const maxNodeDuration = rankedNodes.length > 0 ? rankedNodes[0].avgDuration : 1;

  // ===== 渲染 =====
  return (
    <div className="space-y-3">
      {/* ===== 顶部标题栏 + 工作流选择器 ===== */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Activity size={16} className="text-sky-400" />
          <span className="text-[14px] font-medium text-white">流程效率分析</span>
          <span className="text-[10px] font-mono text-gray-600">
            {hasData
              ? `${filteredExecutions.length} 次执行 · ${efficiency.nodeEfficiencies.length} 节点`
              : "本地模式"}
          </span>
        </div>
        {hasData && (
          <div className="flex items-center gap-1.5">
            <Filter size={11} className="text-gray-500" />
            <div className="relative">
              <select
                value={selectedWorkflow}
                onChange={(e) => setSelectedWorkflow(e.target.value)}
                className="appearance-none pl-2 pr-7 py-1 rounded-md bg-white/[0.03] border border-white/[0.08] text-[11px] font-mono text-gray-200 focus:outline-none focus:border-sky-500/40 cursor-pointer hover:bg-white/[0.05] transition-colors"
              >
                <option value="all" className="bg-[#0a0e1a]">
                  全部工作流 ({executions.length})
                </option>
                {workflowOptions.map((wf) => (
                  <option
                    key={wf.name}
                    value={wf.name}
                    className="bg-[#0a0e1a]"
                  >
                    {wf.name} ({wf.count})
                  </option>
                ))}
              </select>
              <ChevronDown
                size={11}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
              />
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-[12px] text-gray-500">
          <Loader2 size={14} className="animate-spin mr-2" />
          加载中...
        </div>
      ) : !hasData ? (
        <EmptyState
          text="暂无执行数据 · 请先运行工作流"
          sub="工作流执行记录将自动写入 localStorage 并在此聚合分析"
        />
      ) : (
        <>
          {/* ===== 顶部 KPI 卡片区 ===== */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <MiniKpi
              label="平均耗时"
              value={formatDuration(kpis.avgDuration)}
              icon={Clock}
              color="#FBBF24"
              delay={0}
            />
            <MiniKpi
              label="流程成功率"
              value={`${kpis.successRate.toFixed(1)}%`}
              icon={CheckCircle2}
              color="#34D399"
              delay={0.05}
            />
            <MiniKpi
              label="瓶颈节点数"
              value={kpis.bottleneckCount}
              icon={Snail}
              color="#F87171"
              delay={0.1}
            />
            <MiniKpi
              label="自动化率"
              value={`${kpis.automationRate.toFixed(1)}%`}
              icon={Zap}
              color="#60A5FA"
              delay={0.15}
            />
          </div>

          {/* ===== 中部：节点效率排行 + 效率趋势图 ===== */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* 节点效率排行（按耗时排序） */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-3"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <BarChart3 size={13} className="text-sky-400" />
                  <span className="text-[12px] font-medium text-gray-200">
                    节点效率排行
                  </span>
                </div>
                <span className="text-[10px] font-mono text-gray-600">
                  按平均耗时 · 瓶颈标红
                </span>
              </div>
              {rankedNodes.length > 0 ? (
                <div className="max-h-[280px] overflow-y-auto space-y-1.5 pr-1">
                  {rankedNodes.map((node, idx) => {
                    const durPct = Math.min(
                      100,
                      (node.avgDuration / maxNodeDuration) * 100
                    );
                    return (
                      <div
                        key={node.nodeId}
                        className={`rounded-lg border p-2 transition-colors ${
                          node.isBottleneck
                            ? "border-red-500/30 bg-red-500/[0.04]"
                            : "border-white/[0.04] bg-white/[0.01] hover:bg-white/[0.03]"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] font-mono text-gray-600 w-4 shrink-0">
                            {idx + 1}
                          </span>
                          <Cpu
                            size={11}
                            className={
                              node.isBottleneck
                                ? "text-red-400 shrink-0"
                                : "text-gray-500 shrink-0"
                            }
                          />
                          <span
                            className={`text-[11px] font-mono truncate ${
                              node.isBottleneck
                                ? "text-red-300"
                                : "text-gray-200"
                            }`}
                          >
                            {node.nodeName}
                          </span>
                          <span className="text-[9px] font-mono text-gray-600 shrink-0">
                            [{node.nodeType}]
                          </span>
                          {node.isBottleneck && (
                            <span className="ml-auto text-[9px] font-mono px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 border border-red-500/30 shrink-0">
                              瓶颈 {node.bottleneckScore}
                            </span>
                          )}
                        </div>
                        {/* 耗时进度条 */}
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex-1 h-1 rounded-full bg-white/[0.05] overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${durPct}%` }}
                              transition={{
                                duration: 0.5,
                                delay: 0.3 + idx * 0.03,
                              }}
                              className="h-full rounded-full"
                              style={{
                                background: node.isBottleneck
                                  ? "linear-gradient(90deg, #F87171, #EF4444)"
                                  : "linear-gradient(90deg, #FBBF24, #FB923C)",
                              }}
                            />
                          </div>
                          <span className="text-[10px] font-mono text-gray-400 shrink-0 w-12 text-right">
                            {formatDuration(node.avgDuration)}
                          </span>
                        </div>
                        {/* 节点指标明细 */}
                        <div className="flex items-center gap-3 text-[9px] font-mono text-gray-600">
                          <span title="成功率">
                            成功率{" "}
                            <span
                              className={
                                node.successRate >= 0.8
                                  ? "text-emerald-400"
                                  : node.successRate >= 0.5
                                  ? "text-amber-400"
                                  : "text-red-400"
                              }
                            >
                              {(node.successRate * 100).toFixed(0)}%
                            </span>
                          </span>
                          <span title="P95 耗时">
                            P95{" "}
                            <span className="text-gray-400">
                              {formatDuration(node.p95Duration)}
                            </span>
                          </span>
                          <span title="执行次数">
                            执行{" "}
                            <span className="text-gray-400">
                              {node.totalExecutions}
                            </span>
                          </span>
                          <span title="失败次数" className="ml-auto">
                            失败{" "}
                            <span className="text-red-400/80">
                              {node.failedCount}
                            </span>
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <EmptyState text="暂无节点执行数据" />
              )}
            </motion.div>

            {/* 效率趋势图（双轴：耗时 + 成功率） */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.25 }}
              className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-3"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <TrendingUp size={13} className="text-emerald-400" />
                  <span className="text-[12px] font-medium text-gray-200">
                    效率趋势（最近 14 天）
                  </span>
                </div>
                <div className="flex items-center gap-3 text-[10px] font-mono">
                  <span className="flex items-center gap-1 text-amber-400">
                    <span className="w-2 h-2 rounded-full bg-amber-400" />
                    耗时(s)
                  </span>
                  <span className="flex items-center gap-1 text-emerald-400">
                    <span className="w-2 h-2 rounded-full bg-emerald-400" />
                    成功率(%)
                  </span>
                </div>
              </div>
              {trendChartData.some(
                (t) => t.duration > 0 || t.successRate > 0
              ) ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart
                    data={trendChartData}
                    margin={{ top: 5, right: 10, left: -20, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(255,255,255,0.04)"
                    />
                    <XAxis
                      dataKey="label"
                      tick={{
                        fill: "#6B7280",
                        fontSize: 10,
                        fontFamily: "monospace",
                      }}
                      axisLine={{ stroke: "rgba(255,255,255,0.06)" }}
                      tickLine={false}
                    />
                    <YAxis
                      yAxisId="left"
                      tick={{
                        fill: "#6B7280",
                        fontSize: 10,
                        fontFamily: "monospace",
                      }}
                      axisLine={{ stroke: "rgba(255,255,255,0.06)" }}
                      tickLine={false}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      domain={[0, 100]}
                      tick={{
                        fill: "#6B7280",
                        fontSize: 10,
                        fontFamily: "monospace",
                      }}
                      axisLine={{ stroke: "rgba(255,255,255,0.06)" }}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "rgba(15,23,42,0.95)",
                        border: "1px solid rgba(255,255,255,0.08)",
                        borderRadius: 8,
                        fontSize: 11,
                        fontFamily: "monospace",
                      }}
                      labelStyle={{ color: "#9CA3AF" }}
                      formatter={(value: any, name: any) => {
                        if (name === "耗时(s)")
                          return [`${value}s`, name];
                        return [`${value}%`, name];
                      }}
                    />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="durationSec"
                      stroke="#FBBF24"
                      strokeWidth={1.5}
                      dot={{ r: 2, fill: "#FBBF24" }}
                      activeDot={{ r: 4 }}
                      name="耗时(s)"
                      connectNulls
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="successRate"
                      stroke="#34D399"
                      strokeWidth={1.5}
                      dot={{ r: 2, fill: "#34D399" }}
                      activeDot={{ r: 4 }}
                      name="成功率(%)"
                      connectNulls
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState text="暂无趋势数据" sub="最近 14 天无已完成执行" />
              )}
            </motion.div>
          </div>

          {/* ===== 底部：优化建议 + 行业 benchmark 对比 ===== */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* 优化建议清单 */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-3"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Lightbulb size={13} className="text-yellow-400" />
                  <span className="text-[12px] font-medium text-gray-200">
                    优化建议
                  </span>
                </div>
                <span className="text-[10px] font-mono text-gray-600">
                  {suggestions.length} 条建议
                </span>
              </div>
              {suggestions.length > 0 ? (
                <div className="max-h-[300px] overflow-y-auto space-y-1.5 pr-1">
                  {suggestions.map((s, idx) => {
                    const priorityCfg =
                      s.priority === "high"
                        ? {
                            color: "#F87171",
                            bg: "bg-red-500/10",
                            border: "border-red-500/30",
                          }
                        : s.priority === "medium"
                        ? {
                            color: "#FBBF24",
                            bg: "bg-amber-500/10",
                            border: "border-amber-500/30",
                          }
                        : {
                            color: "#60A5FA",
                            bg: "bg-sky-500/10",
                            border: "border-sky-500/30",
                          };
                    const typeLabel: Record<
                      OptimizationSuggestion["type"],
                      string
                    > = {
                      bottleneck: "瓶颈",
                      low_success: "低成功率",
                      high_failure: "高失败率",
                      slow_execution: "慢执行",
                    };
                    return (
                      <motion.div
                        key={`${s.nodeId}_${s.type}_${idx}`}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.35 + idx * 0.04 }}
                        className={`rounded-lg border ${priorityCfg.bg} ${priorityCfg.border} p-2`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className="text-[9px] font-mono px-1.5 py-0.5 rounded shrink-0"
                            style={{
                              color: priorityCfg.color,
                              background: `${priorityCfg.color}15`,
                            }}
                          >
                            {typeLabel[s.type]}
                          </span>
                          <span className="text-[11px] font-mono text-gray-200 truncate">
                            {s.title}
                          </span>
                          <span
                            className="ml-auto text-[9px] font-mono px-1.5 py-0.5 rounded shrink-0"
                            style={{
                              color: priorityCfg.color,
                              border: `1px solid ${priorityCfg.color}40`,
                            }}
                          >
                            {s.priority === "high"
                              ? "高优先"
                              : s.priority === "medium"
                              ? "中优先"
                              : "低优先"}
                          </span>
                        </div>
                        <div className="text-[10px] font-mono text-gray-500 leading-relaxed mb-1">
                          {s.detail}
                        </div>
                        <div className="flex items-center gap-1 text-[10px] font-mono text-emerald-400">
                          <Target size={10} />
                          <span>{s.predictedImprovement}</span>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              ) : (
                <EmptyState
                  text="暂无优化建议"
                  sub="当前节点效率良好，未识别到瓶颈或高失败率"
                />
              )}
            </motion.div>

            {/* 行业 benchmark 对比表 */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.35 }}
              className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-3"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Award size={13} className="text-amber-400" />
                  <span className="text-[12px] font-medium text-gray-200">
                    行业 Benchmark 对比
                  </span>
                </div>
                <span className="text-[10px] font-mono text-gray-600">
                  vs 行业平均 / 标杆
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[10px] font-mono">
                  <thead>
                    <tr className="text-gray-600 uppercase tracking-wider border-b border-white/[0.06]">
                      <th className="text-left py-1.5 px-1 font-normal">指标</th>
                      <th className="text-right py-1.5 px-1 font-normal">
                        当前
                      </th>
                      <th className="text-right py-1.5 px-1 font-normal">
                        行业均
                      </th>
                      <th className="text-right py-1.5 px-1 font-normal">
                        标杆
                      </th>
                      <th className="text-right py-1.5 px-1 font-normal">
                        状态
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {benchmarks.map((b) => {
                      // 格式化当前值
                      const formatVal = (v: number): string => {
                        if (b.metric === "平均流程耗时")
                          return v > 0 ? `${v.toFixed(0)}s` : "—";
                        return `${(v * 100).toFixed(0)}%`;
                      };
                      const statusCfg =
                        b.status === "above"
                          ? {
                              color: "#34D399",
                              label: "优于",
                              bg: "bg-emerald-500/10",
                              border: "border-emerald-500/30",
                            }
                          : b.status === "average"
                          ? {
                              color: "#FBBF24",
                              label: "持平",
                              bg: "bg-amber-500/10",
                              border: "border-amber-500/30",
                            }
                          : {
                              color: "#F87171",
                              label: "落后",
                              bg: "bg-red-500/10",
                              border: "border-red-500/30",
                            };
                      return (
                        <tr
                          key={b.metric}
                          className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors"
                        >
                          <td className="py-2 px-1 text-gray-300">
                            {b.metric}
                          </td>
                          <td className="py-2 px-1 text-right text-white font-bold">
                            {formatVal(b.current)}
                          </td>
                          <td className="py-2 px-1 text-right text-gray-500">
                            {formatVal(b.industryAvg)}
                          </td>
                          <td className="py-2 px-1 text-right text-gray-500">
                            {formatVal(b.bestInClass)}
                          </td>
                          <td className="py-2 px-1 text-right">
                            <span
                              className={`inline-block px-1.5 py-0.5 rounded border ${statusCfg.bg} ${statusCfg.border}`}
                              style={{ color: statusCfg.color }}
                            >
                              {statusCfg.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* 图例说明 */}
              <div className="flex items-center gap-3 mt-2 text-[9px] font-mono text-gray-600">
                <span className="flex items-center gap-1">
                  <Layers size={9} />
                  优于 = 超越行业平均
                </span>
                <span className="flex items-center gap-1">
                  <AlertTriangle size={9} />
                  落后 = 低于行业平均
                </span>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </div>
  );
}
